import type { AuthTokens, ProblemDetails } from './types';

/**
 * The single HTTP client every surface uses.
 *
 * It owns three things the callers should never each solve for themselves: attaching the
 * bearer token, refreshing it exactly once when it expires, and turning an RFC 7807 problem
 * document into a typed error with a message that is safe to show a user.
 */

export class ApiError extends Error {
  constructor(
    override readonly message: string,
    readonly status: number,
    readonly code?: string,
    readonly problem?: ProblemDetails,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level messages, when the server returned a validation problem. */
  get fieldErrors(): Record<string, string[]> | undefined {
    return this.problem?.errors;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** A conflict is usually actionable by the user — a stale record, or a rule they can satisfy. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * The subscription does not cover this, as opposed to the caller not being allowed.
   *
   * Different remedy: a permission problem is solved by an administrator granting a role, a
   * plan limit by changing the subscription. Showing "you are not allowed" for the second
   * sends people to the wrong person.
   */
  get isPlanLimited(): boolean {
    return this.status === 402;
  }

  /**
   * The record moved under the caller. The API answers 412 when an If-Match precondition
   * fails and 409 when a rule found the row already changed; to an editor they are the same
   * situation and want the same message.
   */
  get isStaleWrite(): boolean {
    return this.status === 409 || this.status === 412;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getTokens: () => AuthTokens | null;
  setTokens: (tokens: AuthTokens | null) => void;
  /** Called when a refresh fails and the session is genuinely over. */
  onSessionExpired: () => void;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Sent as If-Match, for optimistic concurrency on updates. */
  etag?: string;
  /** Required by the API for retriable writes. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Skips the bearer token — used by login and the anonymous unsubscribe pages. */
  anonymous?: boolean;
  /** Extra headers, for the few endpoints that take one the client does not model. */
  headers?: Record<string, string>;
  query?: Record<string, unknown>;
}

export class ApiClient {
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(private readonly options: ApiClientOptions) {}

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  /**
   * A GET whose response is plain text rather than JSON.
   *
   * For an endpoint that returns a document — the text pulled out of a PDF, a rendered
   * template — where wrapping it in a JSON envelope would only mean escaping it twice. Failures
   * still arrive as an ApiError, because the problem body is JSON either way.
   */
  async getText(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<string> {
    let response = await this.send(path, { ...options, method: 'GET' });

    if (response.status === 401 && !options.anonymous) {
      const refreshed = await this.refreshOnce();

      if (refreshed) response = await this.send(path, { ...options, method: 'GET' });
      else this.options.onSessionExpired();
    }

    if (!response.ok) {
      await this.materialise<unknown>(response);
      return '';
    }

    return response.text();
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * Fetches a file and hands the browser a save dialog.
   *
   * Not a plain `<a href>`: the export endpoints are authenticated, and a link carries no
   * Authorization header — the browser would follow it anonymously and be refused. Going through
   * the client also means an export inherits the same token refresh and the same error shape as
   * every other call, so a 403 or a "too many rows" refusal arrives as an ApiError a screen can
   * show, rather than as a downloaded file containing an error page.
   *
   * The server names the file in Content-Disposition; that name is honoured when present.
   */
  async download(
    path: string,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
    fallbackFileName = 'download',
  ): Promise<void> {
    let response = await this.send(path, { ...options, method: 'GET' });

    if (response.status === 401 && !options.anonymous) {
      const refreshed = await this.refreshOnce();

      if (refreshed) response = await this.send(path, { ...options, method: 'GET' });
      else this.options.onSessionExpired();
    }

    if (!response.ok) {
      // A failed download still returns the standard problem+json body, so it is materialised
      // through the usual path and thrown as an ApiError.
      await this.materialise<unknown>(response);
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    try {
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download =
        fileNameFromDisposition(response.headers.get('Content-Disposition')) ?? fallbackFileName;

      // Appended before clicking: Firefox ignores a click on a detached element.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Revoked on the next frame rather than immediately — revoking synchronously can cancel
      // the download the click just started.
      requestAnimationFrame(() => URL.revokeObjectURL(url));
    }
  }

  /**
   * Fetches binary content with the caller's token.
   *
   * Needed because `<img src>`, `<a href>` and `<iframe src>` send no Authorization header — the
   * browser issues a plain anonymous request, the API returns 401, and the result is a broken
   * image with nothing in the UI to say why. Anything behind `[Authorize]` has to come through
   * here and be handed to the element as an object URL.
   */
  async fetchBlob(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
    let response = await this.send(path, { ...options, method: 'GET' });

    if (response.status === 401 && !options.anonymous) {
      const refreshed = await this.refreshOnce();

      if (refreshed) response = await this.send(path, { ...options, method: 'GET' });
      else this.options.onSessionExpired();
    }

    if (!response.ok) {
      // Surfaces as the same ApiError shape as every other failed call.
      await this.materialise<unknown>(response);
    }

    return response.blob();
  }

  /**
   * Posts a body and receives binary back.
   *
   * Needed where what to render depends on what the caller is holding — a document preview of
   * settings that have not been saved cannot be a GET, because there is nothing on the server
   * yet to name in a URL.
   */
  async postBlob(
    path: string,
    body: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ): Promise<Blob> {
    let response = await this.send(path, { ...options, method: 'POST', body });

    if (response.status === 401 && !options.anonymous) {
      const refreshed = await this.refreshOnce();

      if (refreshed) response = await this.send(path, { ...options, method: 'POST', body });
      else this.options.onSessionExpired();
    }

    // A refusal comes back as problem+json, so it is materialised through the usual path and
    // thrown as an ApiError rather than handed to the caller as a blob of error text.
    if (!response.ok) await this.materialise<unknown>(response);

    return response.blob();
  }

  /** Multipart upload. The browser sets the boundary, so Content-Type is deliberately unset. */
  async upload<T>(path: string, form: FormData, options: Omit<RequestOptions, 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: options.method ?? 'POST', body: form });
  }

  private async request<T>(path: string, options: RequestOptions): Promise<T> {
    const response = await this.send(path, options);

    // A 401 on an authenticated call means the access token expired. Refresh once and retry;
    // a second 401 means the session is genuinely over and the caller is told.
    if (response.status === 401 && !options.anonymous) {
      const refreshed = await this.refreshOnce();

      if (refreshed) {
        const retry = await this.send(path, options);
        return this.materialise<T>(retry);
      }

      this.options.onSessionExpired();
    }

    return this.materialise<T>(response);
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    // An empty base URL means "same origin", which is the development default: requests go
    // through the dev server's proxy, so the browser never makes a cross-origin request and
    // CORS never enters the picture. `new URL` needs an explicit base to resolve a relative
    // path, hence the second argument.
    const url = new URL(
      path.startsWith('http') ? path : `${this.options.baseUrl}${path}`,
      window.location.origin,
    );

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value === undefined || value === null || value === '') continue;

      // Repeated key for arrays, which is what ASP.NET model binding expects.
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item));
      } else {
        url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    const isFormData = options.body instanceof FormData;

    if (options.body !== undefined && !isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (!options.anonymous) {
      const tokens = this.options.getTokens();
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    if (options.etag) headers['If-Match'] = `"${options.etag}"`;
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

    // Applied last so a caller cannot accidentally drop the Authorization header by passing
    // one of its own, and can still override Content-Type where an endpoint needs it.
    for (const [name, value] of Object.entries(options.headers ?? {})) headers[name] = value;

    try {
      return await fetch(url.toString(), {
        method: options.method ?? 'GET',
        headers,
        body: isFormData
          ? (options.body as FormData)
          : options.body !== undefined
            ? JSON.stringify(options.body)
            : undefined,
        signal: options.signal,
      });
    } catch (caught) {
      // An abort is the caller navigating away, not a fault — it must propagate untouched so
      // useAsync can recognise and ignore it.
      if (caught instanceof DOMException && caught.name === 'AbortError') throw caught;

      // fetch rejects with an opaque "Failed to fetch" for every transport problem: server
      // down, DNS failure, blocked by CORS. Naming the URL that failed turns an unactionable
      // message into one someone can do something about.
      throw new ApiError(
        `Could not reach the ClientMa API at ${url.origin}. ` +
          'Check that the API is running and reachable.',
        0,
        'network.unreachable',
      );
    }
  }

  private async materialise<T>(response: Response): Promise<T> {
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as T;
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    const isJson = contentType.includes('json');

    if (!response.ok) {
      const problem: ProblemDetails | undefined = isJson
        ? await response.json().catch(() => undefined)
        : undefined;

      // Prefer the server's own detail: it is written for the user and usually says what to
      // do next. The status-based fallback is only for a response with no body at all.
      const message =
        problem?.detail ??
        problem?.title ??
        defaultMessageFor(response.status);

      throw new ApiError(message, response.status, problem?.code, problem);
    }

    if (!isJson) return (await response.text()) as T;

    const payload = await response.json();

    // The API returns an ETag on records that support optimistic concurrency. Surfacing it on
    // the payload saves every caller from reading response headers.
    const etag = response.headers.get('ETag');

    if (etag && payload && typeof payload === 'object' && !Array.isArray(payload)) {
      Object.defineProperty(payload, '__etag', {
        value: etag.replace(/^W\//, '').replace(/"/g, ''),
        enumerable: false,
      });
    }

    return payload as T;
  }

  /**
   * Refreshes the access token. Concurrent callers share one in-flight refresh, so a page that
   * fires six requests at once does not burn six refresh tokens.
   */
  private refreshOnce(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      const tokens = this.options.getTokens();
      if (!tokens?.refreshToken) return false;

      try {
        const response = await fetch(`${this.options.baseUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });

        if (!response.ok) {
          this.options.setTokens(null);
          return false;
        }

        const refreshed = (await response.json()) as { tokens?: AuthTokens } | AuthTokens;
        const next = 'tokens' in refreshed ? refreshed.tokens : (refreshed as AuthTokens);

        if (!next?.accessToken) {
          this.options.setTokens(null);
          return false;
        }

        this.options.setTokens(next);
        return true;
      } catch {
        // A network failure is not proof the session ended, so the tokens are left alone and
        // the caller simply sees the original 401.
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }
}

function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'The request could not be processed. Check the details and try again.';
    case 401:
      return 'Your session has expired. Sign in again to continue.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record could not be found.';
    case 409:
      return 'That change conflicts with the current state of the record.';
    case 413:
      return 'The upload is too large.';
    case 429:
      return 'Too many requests. Wait a moment and try again.';
    default:
      return status >= 500
        ? 'Something went wrong on our side. Try again shortly.'
        : 'The request could not be completed.';
  }
}

/** Reads the ETag the client attached to a payload, for a follow-up update. */
export function etagOf(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && '__etag' in payload) {
    return (payload as { __etag?: string }).__etag;
  }

  return undefined;
}

/**
 * The file name a server suggested.
 *
 * Prefers the RFC 5987 `filename*` form, which is the one that survives non-ASCII characters —
 * the plain `filename` is mojibake for anything outside Latin-1.
 */
function fileNameFromDisposition(header: string | null): string | undefined {
  if (!header) return undefined;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);

  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header should not stop the download; the fallback name is used instead.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1];
}
